/**
 * Guardian Groups — the parent's co-parenting circles.
 *
 * A "guardian group" bundles the adults who share a set of kids' YouTube
 * profiles. This page lists each group (name + guardians + kids), lets the
 * caller rename a group, and mints a shareable co-parent invite link.
 *
 * NOTE: this app has no global <ApolloProvider>. The shared Apollo `client`
 * is imported and called directly (client.query / client.mutate), the same
 * pattern InvitePage and the react-query api hooks use. Using Apollo's
 * useQuery/useMutation here throws "no client in context".
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import {client as sharedClient} from '../../api/graphql';
import {
  MY_GUARDIAN_GROUPS,
  RENAME_GUARDIAN_GROUP,
  CREATE_COPARENT_INVITE,
  GET_MY_YOUTUBE_PROFILES,
} from '../../api/graphql_guardian_groups';
import {usePageTitle} from '../../hooks/usePageTitle';

interface GroupProfile {
  id: string;
  name?: string | null;
  thumbnails?: Array<{url?: string | null}> | null;
}
interface GroupMember {
  uid: string;
  displayName?: string | null;
  phoneNumber?: string | null;
  isSelf: boolean;
}
interface GuardianGroup {
  id: string;
  name: string;
  nameIsCustom: boolean;
  createdAt?: string | null;
  members: GroupMember[];
  profiles: GroupProfile[];
}
interface MyProfile {
  id: string;
  name?: string | null;
  thumbnails?: Array<{url?: string | null}> | null;
}

// The `client` prop is accepted for parity with the other page components
// (index.tsx passes it), but this page calls the shared `client` directly —
// there is no ApolloProvider to read it from context.
interface Props {
  client?: unknown;
}

function memberLabel(m: GroupMember): string {
  if (m.isSelf) return 'You';
  return m.displayName || m.phoneNumber || 'Co-parent';
}

function GroupCard({
  group,
  onRename,
}: {
  group: GuardianGroup;
  onRename: (group: GuardianGroup) => void;
}): React.JSX.Element {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        px: 3,
        py: 2.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: '#0F172A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {group.name}
        </Typography>
        <Tooltip title="Rename group">
          <IconButton
            size="small"
            aria-label="Rename group"
            onClick={() => onRename(group)}
            sx={{color: '#94A3B8'}}
          >
            <EditRoundedIcon sx={{fontSize: 17}} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Guardians */}
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{mt: 1.5}}>
        {group.members.map(m => (
          <Chip
            key={m.uid}
            size="small"
            label={memberLabel(m)}
            variant={m.isSelf ? 'filled' : 'outlined'}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              ...(m.isSelf
                ? {bgcolor: '#EEF2FF', color: '#3448F0'}
                : {borderColor: '#E2E8F0', color: '#334155'}),
            }}
          />
        ))}
      </Stack>

      {/* Kids */}
      {group.profiles.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{mt: 2}}>
          <AvatarGroup
            max={6}
            sx={{
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                fontSize: 13,
                border: '2px solid #fff',
              },
            }}
          >
            {group.profiles.map(p => (
              <Avatar key={p.id} src={p.thumbnails?.[0]?.url || undefined} alt={p.name || ''}>
                {(p.name || '?').charAt(0).toUpperCase()}
              </Avatar>
            ))}
          </AvatarGroup>
          <Typography sx={{fontSize: 12, color: '#64748B'}} noWrap>
            {group.profiles.map(p => p.name || 'Untitled').join(', ')}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}

export default function GuardianGroups(_props: Props): React.JSX.Element {
  usePageTitle('Co-parents');

  const [groups, setGroups] = useState<GuardianGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {data} = await sharedClient.query({
        query: MY_GUARDIAN_GROUPS,
        fetchPolicy: 'network-only',
      });
      setGroups((data?.myGuardianGroups ?? []) as GuardianGroup[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your groups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  // Rename dialog state.
  const [renameTarget, setRenameTarget] = useState<GuardianGroup | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const openRename = (group: GuardianGroup) => {
    setRenameTarget(group);
    setRenameValue(group.name);
    setRenameError(null);
  };
  const closeRename = () => {
    if (renameSaving) return;
    setRenameTarget(null);
  };
  const saveRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) {
      setRenameError('Enter a name.');
      return;
    }
    setRenameSaving(true);
    setRenameError(null);
    try {
      const {data} = await sharedClient.mutate({
        mutation: RENAME_GUARDIAN_GROUP,
        variables: {groupId: renameTarget.id, name},
      });
      const updated = data?.renameGuardianGroup as GuardianGroup | undefined;
      if (updated) {
        setGroups(prev => prev.map(g => (g.id === updated.id ? updated : g)));
      }
      setRenameTarget(null);
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Could not rename the group.');
    } finally {
      setRenameSaving(false);
    }
  };

  // Invite dialog state.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [myProfiles, setMyProfiles] = useState<MyProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
  const [inviteGroupName, setInviteGroupName] = useState('');
  const [inviteCreating, setInviteCreating] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const openInvite = async () => {
    setInviteOpen(true);
    setInviteError(null);
    setInviteLink(null);
    setInviteGroupName('');
    setCopied(false);
    setProfilesLoading(true);
    try {
      const {data} = await sharedClient.query({
        query: GET_MY_YOUTUBE_PROFILES,
        fetchPolicy: 'network-only',
      });
      const profiles = (data?.getMyYouTubeProfiles ?? []) as MyProfile[];
      setMyProfiles(profiles);
      // All checked by default.
      const initial: Record<string, boolean> = {};
      for (const p of profiles) initial[p.id] = true;
      setCheckedIds(initial);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not load your kids.');
    } finally {
      setProfilesLoading(false);
    }
  };
  const closeInvite = () => {
    if (inviteCreating) return;
    setInviteOpen(false);
  };

  const checkedProfileIds = useMemo(
    () => Object.keys(checkedIds).filter(id => checkedIds[id]),
    [checkedIds]
  );

  const createInvite = async () => {
    if (checkedProfileIds.length === 0) {
      setInviteError('Pick at least one child to share.');
      return;
    }
    setInviteCreating(true);
    setInviteError(null);
    try {
      const {data} = await sharedClient.mutate({
        mutation: CREATE_COPARENT_INVITE,
        variables: {
          profileIds: checkedProfileIds,
          phoneNumber: null,
          groupName: inviteGroupName.trim() || null,
        },
      });
      const r = data?.createCoparentInvite;
      if (r?.success && r.link) {
        setInviteLink(r.link);
        // Refresh the groups list — a new group may have been created.
        void loadGroups();
      } else {
        setInviteError(r?.message || 'Could not create the invite link.');
      }
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not create the invite link.');
    } finally {
      setInviteCreating(false);
    }
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (permissions / insecure context) — the
      // link is still visible for manual copy, so we just skip the toast.
    }
  };

  const inviteButton = (
    <Button
      variant="contained"
      startIcon={<GroupAddRoundedIcon sx={{fontSize: 18}} />}
      onClick={openInvite}
      sx={{
        borderRadius: 999,
        bgcolor: '#3448F0',
        fontWeight: 700,
        fontSize: 13,
        px: 2.5,
        py: 1,
        textTransform: 'none',
        '&:hover': {bgcolor: '#2839C9'},
      }}
    >
      Invite a co-parent
    </Button>
  );

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 6}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4,
          }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: '#0F172A',
                lineHeight: 1.2,
              }}
            >
              Co-parents
            </Typography>
            <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.5}}>
              Share your kids' YouTube oversight with another guardian.
            </Typography>
          </Box>
          {!loading && groups.length > 0 && inviteButton}
        </Box>

        {error && (
          <Alert severity="error" sx={{mb: 3, borderRadius: 2}}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{py: 8}}>
            <CircularProgress />
            <Typography sx={{fontSize: 13, color: '#64748B'}}>Loading your groups…</Typography>
          </Stack>
        ) : groups.length === 0 ? (
          <Box
            sx={{
              borderRadius: 3,
              border: '2px dashed #E2E8F0',
              bgcolor: '#FFFFFF',
              p: 5,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                mx: 'auto',
                mb: 2.5,
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#EEF2FF',
                color: '#3448F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PeopleAltRoundedIcon sx={{fontSize: 22}} />
            </Box>
            <Typography sx={{fontSize: 18, fontWeight: 800, color: '#0F172A'}}>
              No co-parents yet
            </Typography>
            <Typography
              sx={{
                mx: 'auto',
                mt: 1,
                mb: 3,
                maxWidth: 420,
                fontSize: 13,
                lineHeight: 1.6,
                color: '#64748B',
              }}
            >
              Invite another guardian to review and guide your kids' YouTube together. They'll see
              the same profiles and decisions you do.
            </Typography>
            {inviteButton}
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {groups.map(g => (
              <GroupCard key={g.id} group={g} onRename={openRename} />
            ))}
          </Stack>
        )}
      </Container>

      {/* Rename dialog */}
      <Dialog open={renameTarget !== null} onClose={closeRename} fullWidth maxWidth="xs">
        <DialogTitle sx={{fontWeight: 800}}>Rename group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Group name"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void saveRename();
            }}
            error={Boolean(renameError)}
            helperText={renameError || ' '}
          />
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={closeRename} disabled={renameSaving} sx={{textTransform: 'none'}}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveRename}
            disabled={renameSaving}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              bgcolor: '#3448F0',
              '&:hover': {bgcolor: '#2839C9'},
            }}
          >
            {renameSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={closeInvite} fullWidth maxWidth="xs">
        <DialogTitle sx={{fontWeight: 800}}>Invite a co-parent</DialogTitle>
        <DialogContent>
          {inviteError && (
            <Alert severity="error" sx={{mb: 2, borderRadius: 2}}>
              {inviteError}
            </Alert>
          )}

          {inviteLink ? (
            <Stack spacing={2}>
              <Typography sx={{fontSize: 13, color: '#334155'}}>
                Share this link with your co-parent — they'll be logged in and these kids linked
                automatically.
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#F8FAFC',
                  fontSize: 12,
                  wordBreak: 'break-all',
                  color: '#0F172A',
                }}
              >
                {inviteLink}
              </Box>
              <Button
                variant="contained"
                startIcon={<ContentCopyRoundedIcon sx={{fontSize: 18}} />}
                onClick={copyLink}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  bgcolor: '#3448F0',
                  '&:hover': {bgcolor: '#2839C9'},
                }}
              >
                {copied ? 'Copied!' : 'Copy link'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography sx={{fontSize: 13, color: '#64748B', mb: 0.5}}>
                Choose which kids to share.
              </Typography>
              {profilesLoading ? (
                <Stack alignItems="center" sx={{py: 3}}>
                  <CircularProgress size={24} />
                </Stack>
              ) : myProfiles.length === 0 ? (
                <Typography sx={{fontSize: 13, color: '#64748B', py: 2}}>
                  You don't have any kids linked yet.
                </Typography>
              ) : (
                <Stack>
                  {myProfiles.map(p => (
                    <FormControlLabel
                      key={p.id}
                      control={
                        <Checkbox
                          checked={Boolean(checkedIds[p.id])}
                          onChange={e =>
                            setCheckedIds(prev => ({...prev, [p.id]: e.target.checked}))
                          }
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={p.thumbnails?.[0]?.url || undefined}
                            sx={{width: 26, height: 26, fontSize: 12}}
                          >
                            {(p.name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography sx={{fontSize: 14}}>{p.name || 'Untitled'}</Typography>
                        </Stack>
                      }
                    />
                  ))}
                </Stack>
              )}
              <TextField
                fullWidth
                margin="dense"
                label="Group name (optional)"
                placeholder="e.g. The Smiths"
                value={inviteGroupName}
                onChange={e => setInviteGroupName(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={closeInvite} disabled={inviteCreating} sx={{textTransform: 'none'}}>
            {inviteLink ? 'Done' : 'Cancel'}
          </Button>
          {!inviteLink && (
            <Button
              variant="contained"
              onClick={createInvite}
              disabled={inviteCreating || profilesLoading || myProfiles.length === 0}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                bgcolor: '#3448F0',
                '&:hover': {bgcolor: '#2839C9'},
              }}
            >
              {inviteCreating ? 'Creating…' : 'Create invite link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
